import { BLOCKS } from '@contentful/rich-text-types'
import parseBox from 'services/contentful/parsers/box'
import parseImage from 'services/contentful/parsers/image'
import parseTip from 'services/contentful/parsers/tip'

import type { Block, Text } from '@contentful/rich-text-types'
import type { CTFRulesProps, Element } from '@doist/content-system'

type BlogArticleBox = {
    title: string
    content: string
    type: 'tip' | 'warning' | 'dig-deeper'
    link?: { title: string; href: string }
    illustration: string
}

type BlogEmbedHtml = { html: string }

type Link = {
    title: string
    text: string
    href: string
    target: string
}

type BlogTip = {
    title: string
    description: string
    ctaLink: Link
    captionLink: Link
    backgroundImage: {
        title: string
        description: string
        size: string
        url: string
        src: string
        alt: string
        target: string
    }
}

type Image = {
    title: string
    description: string
    url: string
    size: {
        width: number
        height: number
    }
}

function createCTFBlock(props: Partial<Block>): Block {
    return { data: {}, content: [], ...props } as Block
}

function createCTFText(props: Partial<Text>): Text {
    return { nodeType: 'text', marks: [], data: {}, ...props } as Text
}

function rules({ element: el, tagName, Block, Inline }: CTFRulesProps) {
    if (tagName === 'img') {
        const image = parseImage(el.data.target)
        const { title, description, url, size } = image

        return Block({
            tagName: 'img',
            type: 'element',
            properties: {
                title,
                description,
                url,
                size,
            },
        })
    }

    if (tagName === 'blogEmbedHtml') {
        const html = el.data.target.fields.html

        return Block({
            tagName: 'blogEmbedHtml',
            type: 'element',
            properties: {
                html,
            },
        })
    }

    if (tagName === 'blogTip') {
        const entry = el.data.target
        const data = parseTip(entry)

        return Block({
            tagName: 'blogTip',
            type: 'element',
            properties: {
                title: data.title,
                description: data.description,
                ctaLink: data.ctaLink,
                captionLink: data.captionLink,
                backgroundImage: data.backgroundImage,
            },
        } as Partial<Element>)
    }

    // Unfortunately, Contentful doesn't handle `thead` or `tbody` elements out of the box.
    // We need to create custom blocks instead.
    if (tagName === 'thead') {
        return Block({ tagName: 'thead', children: Inline(el.content) })
    }

    if (tagName === 'tbody') {
        return Block({ tagName: 'tbody', children: Inline(el.content) })
    }

    if (tagName === 'articleTable') {
        const columns = el.data.target.fields.columns as { fields: Record<string, string> }[]

        const headRow = createCTFBlock({ nodeType: BLOCKS.TABLE_ROW })
        const bodyRows: Block[] = []

        columns.forEach(({ fields }) => {
            const keys = Object.keys(fields)

            keys.forEach((key) => {
                if (key === 'title') {
                    headRow.content.push(
                        createCTFBlock({
                            nodeType: BLOCKS.TABLE_HEADER_CELL,
                            content: [createCTFText({ value: fields[key] })],
                        }),
                    )
                    return
                }

                const index = Number(key.replace('row', '')) - 1
                const bodyRow = bodyRows[index] || createCTFBlock({ nodeType: BLOCKS.TABLE_ROW })

                bodyRow.content.push(
                    createCTFBlock({
                        nodeType: BLOCKS.TABLE_CELL,
                        content: [
                            createCTFText({ value: fields[key], data: { isMarkdown: true } }),
                        ],
                    }),
                )

                bodyRows[index] = bodyRow
            })
        })

        const children = [
            createCTFBlock({ nodeType: 'thead' as BLOCKS, content: [headRow] }),
            createCTFBlock({ nodeType: 'tbody' as BLOCKS, content: bodyRows }),
        ]

        return Block({
            tagName: 'articleTable',
            type: 'element',
            children: Inline(children),
        })
    }

    if (tagName === 'blogArticleBox') {
        const { title, content, type = 'tip', link, illustration } = parseBox(el.data.target)
        if (!title || !content) return

        const illusUrl =
            illustration?.src ??
            `${process.env.NEXT_PUBLIC_BASE_URL}/static/inspiration/cta-illustration_book.png`

        return Block({
            tagName: 'blogArticleBox',
            type: 'element',
            properties: {
                title,
                content,
                type,
                link,
                illustration: illusUrl,
            },
        } as Partial<Element>)
    }

    if (tagName === 'blogRichTextCta') {
        const data = el.data.target.fields
        const { title, description, backgroundColor, illustration, callToAction } = data

        return Block({
            tagName: 'blogRichTextCta',
            type: 'element',
            properties: {
                title,
                description,
                backgroundColor,
                illustration,
                callToAction,
            },
        })
    }
    if (tagName === 'blogRichText') {
        return Block({
            tagName: 'blogRichText',
            type: 'element',
            properties: {
                text: el.data.target.fields.text,
                type: el.data.target.fields.type,
            },
        })
    }
}

export { rules }

export type { BlogArticleBox, BlogEmbedHtml, BlogTip, Image }
